// Texas industrial & hazardous waste coding: 30 TAC §335.503 defines the
// 8-digit Texas waste code as a 4-character sequence number + 3-digit form
// code + 1-character classification (H, 1, 2, or 3). Form codes are set by
// 30 TAC §335.521(c) Appendix 3.
// @source https://www.law.cornell.edu/regulations/texas/30-Tex-Admin-Code-SS-335-503
// @source https://www3.tceq.texas.gov/steers/help/ihw/formcodes.html
//   (official TCEQ STEERS enumeration of Appendix 3 form codes, fetched 2026-07-07)
// @source TCEQ RG-22 — Guidelines for the Classification and Coding of
//   Industrial and Hazardous Wastes

export type TxFormCategory =
  | 'lab-packs'
  | 'liquids-inorganic'
  | 'liquids-organic'
  | 'solids-inorganic'
  | 'solids-organic'
  | 'sludges-inorganic'
  | 'sludges-organic'
  | 'gases-inorganic'
  | 'gases-organic'
  | 'plant-refuse';

export interface TxFormCodeEntry {
  code: string;
  description: string;
  category: TxFormCategory;
}

export type TxClassificationCode = 'H' | '1' | '2' | '3';

export interface TxClassification {
  code: TxClassificationCode;
  title: string;
  summary: string;
  citation: string;
}

export const TX_FORM_CATEGORIES: Record<TxFormCategory, { title: string; blurb: string }> = {
  'lab-packs': {
    title: 'Lab Packs',
    blurb: 'Containerized collections of small laboratory chemical quantities, packed for transport and disposal. The form code distinguishes old chemicals, debris, mixed packs, acute hazardous contents and waste pharmaceuticals.',
  },
  'liquids-inorganic': {
    title: 'Inorganic Liquids',
    blurb: 'Aqueous and acid/caustic waste streams: spent acids and caustics, cyanide-bearing solutions, scrubber water, leachate and brines. Metal and cyanide content drives both the form code and the classification.',
  },
  'liquids-organic': {
    title: 'Organic Liquids',
    blurb: 'Solvent-bearing and oily liquid streams: halogenated and non-halogenated solvents, oil-water emulsions, waste oil, paint and ink liquids, thinners and PCB-bearing liquids.',
  },
  'solids-inorganic': {
    title: 'Inorganic Solids',
    blurb: 'Contaminated soils, ash and slag, lime solids, metal scale and scrap, drums and containers, batteries, spent filters and adsorbents, asbestos debris, reactive salts and catalyst waste.',
  },
  'solids-organic': {
    title: 'Organic Solids',
    blurb: 'Pesticide solids, resins and polymerized organics, spent carbon, contaminated containers, wood debris, petroleum-contaminated solids, dewatered biological sludges and PCB solids.',
  },
  'sludges-inorganic': {
    title: 'Inorganic Sludges',
    blurb: 'Lime and metal hydroxide sludges, wastewater treatment and plating sludges, air pollution control device sludge, lagoon sediments, drilling mud and brine sludges.',
  },
  'sludges-organic': {
    title: 'Organic Sludges',
    blurb: 'Still bottoms, oily sludge, paint and ink sludge, tars and resins, biological treatment sludges, grease and petroleum-contaminated sludges.',
  },
  'gases-inorganic': {
    title: 'Inorganic Gases',
    blurb: 'Compressed or containerized inorganic gas waste streams.',
  },
  'gases-organic': {
    title: 'Organic Gases',
    blurb: 'Compressed or containerized organic gas waste streams.',
  },
  'plant-refuse': {
    title: 'Plant Refuse',
    blurb: 'Class 2 facility waste: supplemental plant production refuse and routine plant trash (paper, cardboard, packaging, food waste, glass, metals, plastics).',
  },
};

// @source 30 TAC §335.504-§335.507; summaries condensed from the regulation text.
export const TX_CLASSIFICATIONS: Record<TxClassificationCode, TxClassification> = {
  H: {
    code: 'H',
    title: 'Hazardous Waste',
    summary: 'Waste that is listed or characteristic hazardous waste under the federal RCRA rules (40 CFR Part 261), as adopted by reference in Texas. Carries EPA waste codes (D, F, K, P, U) alongside the Texas code.',
    citation: '30 TAC §335.504; 40 CFR Part 261',
  },
  '1': {
    code: '1',
    title: 'Class 1 Industrial Waste',
    summary: 'Nonhazardous industrial waste that is potentially threatening: Class 1 toxic constituents at or above maximum leachable concentrations, ignitable (liquid flash point below 150 F or readily ignitable solid), corrosive (pH 2 or below, or 12.5 or above), 20 ppm or more total recoverable cyanides, or lacking the data to prove a lower class.',
    citation: '30 TAC §335.505',
  },
  '2': {
    code: '2',
    title: 'Class 2 Industrial Waste',
    summary: 'The default nonhazardous class: any industrial solid waste that does not meet the definition of hazardous, Class 1, or Class 3. Most routine industrial waste streams classify as Class 2.',
    citation: '30 TAC §335.506',
  },
  '3': {
    code: '3',
    title: 'Class 3 Industrial Waste',
    summary: 'Inert and essentially insoluble industrial waste posing no threat to human health or the environment: rock, brick, glass, dirt and certain plastics and rubber. Requires leachate testing showing no exceedances and no detectable TPH or PCBs.',
    citation: '30 TAC §335.507',
  },
};

const e = (code: string, description: string, category: TxFormCategory): TxFormCodeEntry =>
  ({ code, description, category });

// Full Appendix 3 enumeration (131 codes) from the TCEQ STEERS help page.
export const TX_FORM_CODES: TxFormCodeEntry[] = [
  // LAB PACKS
  e('001', 'Lab packs of old chemicals only', 'lab-packs'),
  e('002', 'Lab packs of debris only', 'lab-packs'),
  e('003', 'Mixed lab packs', 'lab-packs'),
  e('004', 'Lab packs containing acute hazardous wastes', 'lab-packs'),
  e('005', 'Waste pharmaceuticals managed as hazardous waste', 'lab-packs'),
  e('009', 'Other lab pack (specify in the waste description)', 'lab-packs'),
  // LIQUIDS - INORGANIC
  e('101', 'Aqueous waste with low solvents', 'liquids-inorganic'),
  e('102', 'Aqueous waste with low other toxic organics', 'liquids-inorganic'),
  e('103', 'Spent acid with metals', 'liquids-inorganic'),
  e('104', 'Spent acid without metals', 'liquids-inorganic'),
  e('105', 'Acidic aqueous waste', 'liquids-inorganic'),
  e('106', 'Caustic solution with metals but no cyanides', 'liquids-inorganic'),
  e('107', 'Caustic solution with metals and cyanides', 'liquids-inorganic'),
  e('108', 'Caustic solution with cyanides but no metals', 'liquids-inorganic'),
  e('109', 'Spent caustic', 'liquids-inorganic'),
  e('110', 'Caustic aqueous waste', 'liquids-inorganic'),
  e('111', 'Aqueous waste with reactive sulfides', 'liquids-inorganic'),
  e('112', 'Aqueous waste with other reactives (e.g., explosives)', 'liquids-inorganic'),
  e('113', 'Other aqueous waste with high dissolved solids', 'liquids-inorganic'),
  e('114', 'Other aqueous waste with low dissolved solids', 'liquids-inorganic'),
  e('115', 'Scrubber water', 'liquids-inorganic'),
  e('116', 'Leachate', 'liquids-inorganic'),
  e('117', 'Waste liquid mercury', 'liquids-inorganic'),
  e('119', 'Other inorganic liquids (specify in the waste description)', 'liquids-inorganic'),
  e('198', 'Nonhazardous photographic chemical wastes (inorganic)', 'liquids-inorganic'),
  e('199', 'Brine solution that could also bear the form code 113', 'liquids-inorganic'),
  // LIQUIDS - ORGANIC
  e('200', 'Still bottoms in liquid form (fluid but not sludge)', 'liquids-organic'),
  e('201', 'Concentrated solvent-water solution', 'liquids-organic'),
  e('202', 'Halogenated (e.g., chlorinated) solvent', 'liquids-organic'),
  e('203', 'Non-halogenated solvent', 'liquids-organic'),
  e('204', 'Halogenated/non-halogenated solvent mixture', 'liquids-organic'),
  e('205', 'Oil-water emulsion or mixture', 'liquids-organic'),
  e('206', 'Waste oil (not used oil per 30 TAC 324)', 'liquids-organic'),
  e('207', 'Concentrated aqueous solution of other organics', 'liquids-organic'),
  e('208', 'Concentrated phenolics', 'liquids-organic'),
  e('209', 'Organic paint, ink, lacquer, or varnish', 'liquids-organic'),
  e('210', 'Adhesives or epoxies', 'liquids-organic'),
  e('211', 'Paint thinner or petroleum distillates', 'liquids-organic'),
  e('212', 'Reactive or polymerizeable organic liquids', 'liquids-organic'),
  e('219', 'Other organic liquids (specify in waste description)', 'liquids-organic'),
  e('296', 'Ethylene glycol based antifreeze', 'liquids-organic'),
  e('297', 'Nonhazardous liquids with >= 50 and < 500 ppm PCBs', 'liquids-organic'),
  e('298', 'Nonhazardous liquids with >= 500 ppm PCBs', 'liquids-organic'),
  e('299', 'Nonhazardous photographic chemical waste (organic)', 'liquids-organic'),
  // SOLIDS - INORGANIC
  e('301', 'Soil contaminated with organics', 'solids-inorganic'),
  e('302', 'Soil contaminated with inorganics only', 'solids-inorganic'),
  e('303', 'Ash, slag or other residue from incineration of waste', 'solids-inorganic'),
  e('304', 'Other "dry" ash, slag or thermal residue', 'solids-inorganic'),
  e('305', '"Dry" lime or metal hydroxide solids chemically "fixed"', 'solids-inorganic'),
  e('306', '"Dry" lime or metal hydroxide solids not "fixed"', 'solids-inorganic'),
  e('307', 'Metal scale, filings or scrap', 'solids-inorganic'),
  e('308', 'Empty or crushed metal drums or containers', 'solids-inorganic'),
  e('309', 'Batteries or battery parts, casings, cores', 'solids-inorganic'),
  e('310', 'Spent solid filters or adsorbents', 'solids-inorganic'),
  e('311', 'Asbestos solids and debris', 'solids-inorganic'),
  e('312', 'Metal-cyanide salts/chemicals', 'solids-inorganic'),
  e('313', 'Reactive cyanide salts/chemicals', 'solids-inorganic'),
  e('314', 'Reactive sulfide salts/chemicals', 'solids-inorganic'),
  e('315', 'Other reactive salts/chemicals', 'solids-inorganic'),
  e('316', 'Other metal salts/chemicals', 'solids-inorganic'),
  e('319', 'Other waste inorganic solids (specify in the waste description)', 'solids-inorganic'),
  e('388', 'Empty or crushed glass containers', 'solids-inorganic'),
  e('389', 'Nonhazardous sandblasting waste', 'solids-inorganic'),
  e('390', 'Nonhazardous concrete/cement/construction debris', 'solids-inorganic'),
  e('391', 'Nonhazardous dewatered wastewater treatment sludge', 'solids-inorganic'),
  e('392', 'Nonhazardous dewatered air pollution control device sludge', 'solids-inorganic'),
  e('393', 'Catalyst waste', 'solids-inorganic'),
  e('394', 'Nonhazardous solids with >= 50 ppm and < 500 ppm PCBs', 'solids-inorganic'),
  e('395', 'Nonhazardous solids with >= 500 ppm PCBs', 'solids-inorganic'),
  e('396', 'Nonhazardous electrical equipment/devices with >= 50 ppm and < 500 ppm PCBs', 'solids-inorganic'),
  e('397', 'Nonhazardous electrical equipment/devices with >= 500 ppm PCBs', 'solids-inorganic'),
  e('398', 'Nonhazardous soils with >= 50 ppm and < 500 ppm PCBs', 'solids-inorganic'),
  e('399', 'Nonhazardous soils with >= 500 ppm PCBs', 'solids-inorganic'),
  // SOLIDS - ORGANIC
  e('401', 'Halogenated pesticide solid', 'solids-organic'),
  e('402', 'Non-halogenated pesticide solid', 'solids-organic'),
  e('403', 'Solids resins or polymerized organics', 'solids-organic'),
  e('404', 'Spent carbon', 'solids-organic'),
  e('405', 'Reactive organic solid', 'solids-organic'),
  e('406', 'Empty fiber or plastic containers', 'solids-organic'),
  e('407', 'Other halogenated organic solids (specify in the waste description)', 'solids-organic'),
  e('409', 'Other non-halogenated organic solids (specify in the waste description)', 'solids-organic'),
  e('488', 'Wood debris', 'solids-organic'),
  e('489', 'Petroleum contaminated solids', 'solids-organic'),
  e('490', 'Sandblasting waste', 'solids-organic'),
  e('491', 'Dewatered biological treatment sludge', 'solids-organic'),
  e('492', 'Dewatered sewage or other untreated biological sludge', 'solids-organic'),
  e('493', 'Catalyst waste', 'solids-organic'),
  e('494', 'Solids containing >= 50 ppm and < 500 ppm PCBs', 'solids-organic'),
  e('495', 'Solids containing >= 500 ppm PCBs', 'solids-organic'),
  e('496', 'Electrical equipment/devices with >= 50 ppm and < 500 ppm PCBs', 'solids-organic'),
  e('497', 'Electrical equipment/devices with >= 500 ppm PCBs', 'solids-organic'),
  e('498', 'Soils containing >= 50 ppm and < 500 ppm PCBs', 'solids-organic'),
  e('499', 'Soils containing >= 500 ppm PCBs', 'solids-organic'),
  // SLUDGES - INORGANIC
  e('501', 'Lime sludge without metals', 'sludges-inorganic'),
  e('502', 'Lime sludge with metals/metal hydroxide sludge', 'sludges-inorganic'),
  e('503', 'Wastewater treatment sludge with toxic organics', 'sludges-inorganic'),
  e('504', 'Other wastewater treatment sludge', 'sludges-inorganic'),
  e('505', 'Untreated plating sludge without cyanides', 'sludges-inorganic'),
  e('506', 'Untreated plating sludge with cyanides', 'sludges-inorganic'),
  e('507', 'Other sludge with cyanides', 'sludges-inorganic'),
  e('508', 'Sludge with reactive sulfides', 'sludges-inorganic'),
  e('509', 'Sludge with other reactives', 'sludges-inorganic'),
  e('510', 'Degreasing sludge with metal scale or filings', 'sludges-inorganic'),
  e('511', 'Air pollution control device sludge (e.g., fly ash, wet scrubber sludge)', 'sludges-inorganic'),
  e('512', 'Sediment or lagoon dragout contaminated with organics', 'sludges-inorganic'),
  e('513', 'Sediment or lagoon dragout contaminated with inorganics only', 'sludges-inorganic'),
  e('514', 'Drilling mud', 'sludges-inorganic'),
  e('515', 'Asbestos slurry or sludge', 'sludges-inorganic'),
  e('516', 'Chloride or other brine sludge', 'sludges-inorganic'),
  e('519', 'Other inorganic sludges (specify in the waste description)', 'sludges-inorganic'),
  e('597', 'Catalyst waste', 'sludges-inorganic'),
  e('598', 'Nonhazardous sludges containing >= 50 ppm and < 500 ppm PCBs', 'sludges-inorganic'),
  e('599', 'Nonhazardous sludge containing >= 500 ppm PCBs', 'sludges-inorganic'),
  // SLUDGES - ORGANIC
  e('601', 'Still bottoms of halogenated (e.g., chlorinated) solvents or other organic liquids', 'sludges-organic'),
  e('602', 'Still bottoms of non-halogenated solvents or other organic liquids', 'sludges-organic'),
  e('603', 'Oily sludge', 'sludges-organic'),
  e('604', 'Organic paint or ink sludge', 'sludges-organic'),
  e('605', 'Reactive or polymerizable organics', 'sludges-organic'),
  e('606', 'Resins, tars or tarry sludge', 'sludges-organic'),
  e('607', 'Biological treatment sludge', 'sludges-organic'),
  e('608', 'Sewage or other untreated biological sludge', 'sludges-organic'),
  e('609', 'Other organic sludges (specify in the waste description)', 'sludges-organic'),
  e('695', 'Petroleum contaminated sludges other than still bottoms and oily sludge', 'sludges-organic'),
  e('696', 'Grease', 'sludges-organic'),
  e('697', 'Catalyst waste', 'sludges-organic'),
  e('698', 'Nonhazardous sludges containing >= 50 ppm and < 500 ppm PCBs', 'sludges-organic'),
  e('699', 'Nonhazardous sludges containing >= 500 ppm PCBs', 'sludges-organic'),
  // GASES
  e('701', 'Inorganic gas', 'gases-inorganic'),
  e('801', 'Organic gas', 'gases-organic'),
  // PLANT REFUSE
  e('902', 'Supplemental plant production refuse Class 2 waste from production, manufacturing, or laboratory operations', 'plant-refuse'),
  e('999', 'Plant trash Class 2 waste from facility operations composed of paper, cardboard, packaging materials, food waste, glass, metals, plastics, and related materials', 'plant-refuse'),
];

const byCode = new Map(TX_FORM_CODES.map((entry) => [entry.code, entry]));

export function getTxFormCode(code: string): TxFormCodeEntry | undefined {
  const trimmed = code.trim();
  if (!/^\d{1,3}$/.test(trimmed)) return undefined;
  return byCode.get(trimmed.padStart(3, '0'));
}

export function getTxCategoryEntries(category: TxFormCategory): TxFormCodeEntry[] {
  return TX_FORM_CODES.filter((entry) => entry.category === category);
}
